import {
  ROUTE_ARROW_IMAGE_ID_PREFIX,
  ROUTE_ARROW_IMAGE_PIXEL_RATIO,
  ROUTE_ARROW_IMAGE_SIZE_PX,
} from "../route-block-maplibre.constants";
import { TMapLibreImageHost } from "./maplibre-missing-style-image.models";

/** Per-route-color arrow icon id (one baked-color image per line color). */
export const routeArrowImageId = (color: string): string =>
  `${ROUTE_ARROW_IMAGE_ID_PREFIX}${color}`;

/**
 * Registers a right-pointing "►" arrow icon baked in `color` so a `symbol` layer
 * (`symbol-placement: line`) can draw GPU direction arrows along a route line —
 * the MapLibre twin of the Leaflet leaflet-textpath "►". The raster style ships
 * no sprite/glyphs, so the glyph is drawn to a canvas and registered at runtime
 * as a plain (non-SDF) image, which renders reliably where an SDF alpha mask does
 * not. Idempotent; safe to call eagerly and again from `Map.setMissingStyleImageResolver`
 * (v6 does not resolve missing icons from a `styleimagemissing` listener).
 *
 * The image is intentionally never removed: it is reused by every route of that
 * color and across the per-poll re-render, so the registered set is bounded by the
 * (small) route-color palette. A per-component `removeImage` would instead churn an
 * add + remove on every tracking poll (routes remount each poll), so caching is the
 * cheaper, correct behavior.
 */
export const ensureRouteArrowImage = (
  map: TMapLibreImageHost,
  { color }: { color: string },
): void => {
  const imageId = routeArrowImageId(color);
  if (map.hasImage(imageId)) {
    return;
  }

  const ratio = ROUTE_ARROW_IMAGE_PIXEL_RATIO;
  const size = ROUTE_ARROW_IMAGE_SIZE_PX * ratio;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }

  // Solid right-pointing triangle (the "►" glyph) painted in the route color;
  // symbol-placement:line then rotates it to the direction of travel.
  /* eslint-disable @typescript-eslint/no-magic-numbers -- canvas geometry ratios for the arrow glyph */
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(size * 0.3, size * 0.2);
  ctx.lineTo(size * 0.78, size * 0.5);
  ctx.lineTo(size * 0.3, size * 0.8);
  ctx.closePath();
  ctx.fill();
  /* eslint-enable @typescript-eslint/no-magic-numbers */

  map.addImage(imageId, ctx.getImageData(0, 0, size, size), {
    pixelRatio: ratio,
  });
};
